<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";

import { ApiError } from "@/api/client";
import { describeError } from "@/utils/errors";
import { useAuthStore } from "@/stores/auth";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const auth = useAuthStore();

const email = ref("");
const password = ref("");
const submitting = ref(false);
const errorMessage = ref<string | null>(null);

// `redirect` is propagated by the `requiresAuth` guard so users land
// where they were originally heading after sign-in.
const redirectTo = computed(() => {
  const raw = route.query.redirect;
  return typeof raw === "string" && raw.startsWith("/") ? raw : "/";
});

async function submit() {
  errorMessage.value = null;
  submitting.value = true;
  try {
    await auth.login(email.value, password.value);
    await router.replace(redirectTo.value);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      errorMessage.value = t("login.invalid_credentials");
    } else {
      errorMessage.value = describeError(err, t);
    }
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div class="login">
    <form class="login__card" @submit.prevent="submit">
      <h1>{{ t("login.title") }}</h1>
      <label>
        <span>{{ t("login.email") }}</span>
        <input
          v-model="email"
          type="email"
          autocomplete="username"
          required
          :disabled="submitting"
        />
      </label>
      <label>
        <span>{{ t("login.password") }}</span>
        <input
          v-model="password"
          type="password"
          autocomplete="current-password"
          required
          :disabled="submitting"
        />
      </label>
      <p v-if="errorMessage" class="login__error">{{ errorMessage }}</p>
      <button type="submit" :disabled="submitting">
        {{ submitting ? t("login.submitting") : t("login.submit") }}
      </button>
    </form>
    <p class="login__download">
      <a href="/downloads/nythronix-agent-setup.exe" data-test="download-windows">
        ⬇ {{ t("login.download_windows") }}
      </a>
    </p>
  </div>
</template>

<style scoped>
.login {
  display: grid;
  place-items: center;
  min-height: 70vh;
  padding: 2rem;
}
.login__card {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: min(360px, 100%);
  padding: 2rem;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-bg-elev);
}
.login__card h1 {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
}
.login__card label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.85rem;
}
.login__card input {
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-bg);
  color: var(--color-fg);
  font-family: inherit;
  font-size: 0.95rem;
}
.login__card button {
  padding: 0.6rem 0.75rem;
  border-radius: 4px;
  background: var(--color-accent);
  color: white;
  border: none;
  cursor: pointer;
  font-weight: 600;
}
.login__card button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.login__error {
  margin: 0;
  color: var(--color-error);
  font-size: 0.85rem;
}
.login__download {
  margin: 1rem 0 0;
  font-size: 0.85rem;
  text-align: center;
  opacity: 0.8;
}
</style>
